/**
 * SEO utility functions for generating structured data and meta information
 */

export interface GalleryStructuredData {
  "@context": string;
  "@type": string;
  name: string;
  description?: string;
  image?: string[];
  datePublished?: string;
  dateModified?: string;
  author: {
    "@type": string;
    name: string;
  };
}

export interface OrganizationStructuredData {
  "@context": string;
  "@type": string;
  name: string;
  url: string;
  logo?: string;
  description?: string;
  sameAs?: string[];
}

export interface ImageStructuredData {
  "@context": string;
  "@type": string;
  contentUrl: string;
  description?: string;
  creator: {
    "@type": string;
    name: string;
  };
}

/**
 * Generate structured data for a photography business/organization
 */
export function generateOrganizationStructuredData(
  url: string = "https://mariahblue.com",
  logo?: string
): OrganizationStructuredData {
  return {
    "@context": "https://schema.org",
    "@type": "Photographer",
    name: "Mariah Blue Photography",
    url,
    logo: logo || `${url}/favicon.png`,
    description:
      "Professional photography portfolio by Mariah Blue. Specializing in authentic, unscripted moments captured with intention and care.",
  };
}

/**
 * Generate structured data for a gallery/album
 */
export function generateGalleryStructuredData(
  title: string,
  description?: string,
  images?: string[],
  datePublished?: string,
  dateModified?: string
): GalleryStructuredData {
  return {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    name: title,
    description,
    image: images || [],
    datePublished,
    dateModified: dateModified || datePublished,
    author: {
      "@type": "Person",
      name: "Mariah Blue",
    },
  };
}

/**
 * Generate structured data for a single image
 */
export function generateImageStructuredData(
  imageUrl: string,
  description?: string
): ImageStructuredData {
  return {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    contentUrl: imageUrl,
    description,
    creator: {
      "@type": "Person",
      name: "Mariah Blue",
    },
  };
}

/**
 * Generate breadcrumb structured data
 */
export function generateBreadcrumbStructuredData(
  items: Array<{ name: string; url: string }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Generate Review structured data for testimonials
 */
export function generateReviewStructuredData(
  authorName: string,
  reviewBody: string,
  rating?: number,
  datePublished?: string,
  authorTitle?: string
) {
  const review: any = {
    "@context": "https://schema.org",
    "@type": "Review",
    author: {
      "@type": "Person",
      name: authorName,
    },
    reviewBody: reviewBody,
  };

  if (rating) {
    review.reviewRating = {
      "@type": "Rating",
      ratingValue: rating,
      bestRating: 5,
      worstRating: 1,
    };
  }

  if (datePublished) {
    review.datePublished = datePublished;
  }

  if (authorTitle) {
    review.author.jobTitle = authorTitle;
  }

  return review;
}

/**
 * Generate AggregateRating structured data from multiple reviews
 */
export function generateAggregateRatingStructuredData(
  reviews: Array<{ rating: number }>,
  reviewCount?: number
) {
  const validRatings = reviews.filter((r) => r.rating != null).map((r) => r.rating!);
  if (validRatings.length === 0) {
    return null;
  }

  const averageRating =
    validRatings.reduce((sum, rating) => sum + rating, 0) / validRatings.length;
  const count = reviewCount || validRatings.length;

  return {
    "@context": "https://schema.org",
    "@type": "AggregateRating",
    ratingValue: averageRating.toFixed(1),
    bestRating: "5",
    worstRating: "1",
    ratingCount: count,
  };
}

/**
 * Generate FAQ structured data
 */
export function generateFAQStructuredData(
  questions: Array<{ question: string; answer: string }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map((qa) => ({
      "@type": "Question",
      name: qa.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: qa.answer,
      },
    })),
  };
}

/**
 * Get the base URL for the site
 */
export function getBaseUrl(): string {
  if (typeof window !== "undefined") {
    return window.location.origin;
  }
  return "https://mariahblue.com";
}

/**
 * Get full URL for a path
 */
export function getFullUrl(path: string): string {
  const baseUrl = getBaseUrl();
  return `${baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
}
